/**
 * Size Chart Popup Module
 * Handles the functionality for the size chart popup
 */
class SizeChartPopup {
  constructor() {
    this.sizeChartTriggers = document.querySelectorAll('.size-chart-trigger');
    this.popupContainer = null;
    this.csvData = null;
    this.currentProductHandle = null;

    if (this.sizeChartTriggers.length > 0) {
      this.init();
    }
  }

  init() {
    // Get current product handle from the page
    this.getCurrentProductHandle();
    
    // Create popup container if it doesn't exist
    if (!document.querySelector('.size-chart-popup')) {
      this.createPopupContainer();
    } else {
      this.popupContainer = document.querySelector('.size-chart-popup');
    }

    // Attach event listeners
    this.sizeChartTriggers.forEach(trigger => {
      trigger.addEventListener('click', this.openPopup.bind(this));
    });

    // Close on outside click
    this.popupContainer.addEventListener('click', (event) => {
      if (event.target === this.popupContainer) {
        this.closePopup();
      }
    });

    // Close on escape key
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && this.popupContainer.classList.contains('active')) {
        this.closePopup();
      }
    });

    // Initial category selection (if categories exist)
    this.initializeCategories();
  }

  getCurrentProductHandle() {
    // Try to get product handle from various sources
    const productJson = document.querySelector('[data-product-json]');
    if (productJson) {
      try {
        const product = JSON.parse(productJson.textContent);
        this.currentProductHandle = product.handle;
        console.log('Product handle found from JSON:', this.currentProductHandle);
        return;
      } catch (e) {
        console.warn('Could not parse product JSON:', e);
      }
    }

    // Fallback: try to get from URL or meta tags
    const metaProduct = document.querySelector('meta[property="product:handle"]');
    if (metaProduct) {
      this.currentProductHandle = metaProduct.getAttribute('content');
      console.log('Product handle found from meta:', this.currentProductHandle);
      return;
    }

    // Last fallback: extract from URL path
    const pathParts = window.location.pathname.split('/');
    const productIndex = pathParts.indexOf('products');
    if (productIndex !== -1 && pathParts[productIndex + 1]) {
      this.currentProductHandle = pathParts[productIndex + 1];
      console.log('Product handle found from URL:', this.currentProductHandle);
      return;
    }

    console.warn('Could not determine product handle from any source');
  }

  async fetchCSVData() {
    if (this.csvData) {
      return this.csvData;
    }

    try {
      const response = await fetch('https://cdn.shopify.com/s/files/1/1135/2358/files/SizingSheet_3.csv?v=1773979006');
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const csvText = await response.text();
      console.log('CSV fetched successfully, length:', csvText.length);
      this.csvData = this.parseCSV(csvText);
      console.log('CSV parsed, rows found:', this.csvData.length);
      return this.csvData;
    } catch (error) {
      console.error('Error fetching CSV data:', error);
      return null;
    }
  }

  parseCSV(csvText) {
    const lines = csvText.split('\n');
    const headers = ['Sizing_Table', 'Handle', 'Title', 'Sizing']; // Known headers from the CSV
    const data = [];
    
    let currentRow = '';
    let inQuotes = false;
    
    for (let i = 1; i < lines.length; i++) { // Skip header row
      const line = lines[i];
      
      if (!line.trim()) continue;
      
      currentRow += (currentRow ? '\n' : '') + line;
      
      // Count quotes to determine if we're inside a quoted field
      const quoteCount = (currentRow.match(/"/g) || []).length;
      inQuotes = quoteCount % 2 !== 0;
      
      // If we're not in quotes, try to parse the row
      if (!inQuotes) {
        const parsedRow = this.parseCSVRow(currentRow);
        if (parsedRow && parsedRow.length >= 3) { // At least Sizing_Table, Handle, Title
          const row = {};
          headers.forEach((header, index) => {
            row[header] = parsedRow[index] || '';
          });
          
          // Only add rows that have a handle
          if (row.Handle && row.Handle.trim()) {
            data.push(row);
            console.log('Added row for handle:', row.Handle);
          }
        }
        currentRow = '';
      }
    }
    
    return data;
  }

  parseCSVRow(line) {
    const result = [];
    let current = '';
    let inQuotes = false;
    let i = 0;
    
    while (i < line.length) {
      const char = line[i];
      
      if (char === '"') {
        if (inQuotes && line[i + 1] === '"') {
          // Escaped quote
          current += '"';
          i += 2;
        } else {
          // Toggle quote state
          inQuotes = !inQuotes;
          i++;
        }
      } else if (char === ',' && !inQuotes) {
        // Field separator
        result.push(current.trim());
        current = '';
        i++;
      } else {
        current += char;
        i++;
      }
    }
    
    // Add the last field
    result.push(current.trim());
    
    return result;
  }

  createPopupContainer() {
    // Create main popup container
    this.popupContainer = document.createElement('div');
    this.popupContainer.className = 'size-chart-popup';
    
    // Create popup content
    const popupContent = document.createElement('div');
    popupContent.className = 'size-chart-popup__content';
    
    // Add close button
    const closeButton = document.createElement('button');
    closeButton.className = 'size-chart-popup__close';
    closeButton.setAttribute('aria-label', 'Close size chart');
    closeButton.innerHTML = '<svg viewBox="0 0 14 14" xmlns="http://www.w3.org/2000/svg"><path d="M14 1.41L12.59 0L7 5.59L1.41 0L0 1.41L5.59 7L0 12.59L1.41 14L7 8.41L12.59 14L14 12.59L8.41 7L14 1.41Z" fill="currentColor"/></svg>';
    closeButton.addEventListener('click', this.closePopup.bind(this));
    
    // Add header
    const header = document.createElement('div');
    header.className = 'size-chart-popup__header';
    const title = document.createElement('h2');
    title.className = 'size-chart-popup__title';
    title.textContent = 'Size Chart';
    header.appendChild(title);
    
    // Add content
    const body = document.createElement('div');
    body.className = 'size-chart-popup__body';
    body.innerHTML = '<div class="loading-message">Loading size chart...</div>';
    
    // Assemble the popup
    popupContent.appendChild(closeButton);
    popupContent.appendChild(header);
    popupContent.appendChild(body);
    this.popupContainer.appendChild(popupContent);
    
    // Add to document
    document.body.appendChild(this.popupContainer);
  }

  async getSizeChartContent() {
          // NEW SYSTEM - PRODUCT METAFIELD

      if (
        window.productSizeChartHtml &&
        window.productSizeChartHtml.trim() !== ''
      ) {

        console.log('Loading size chart from metafield');

        return `
          <div class="size-chart-content">
            ${window.productSizeChartHtml}
          </div>
        `;
      }
           // END NEW SYSTEM - PRODUCT METAFIELD
    if (!this.currentProductHandle) {
      console.error('No product handle found');
      return '<div class="error-message">Product information not available.<br><small>Debug: No product handle detected</small></div>';
    }

    const csvData = await this.fetchCSVData();
    if (!csvData) {
      console.error('No CSV data available');
      return '<div class="error-message">Unable to load size chart data.<br><small>Debug: CSV fetch failed</small></div>';
    }

    console.log('Looking for handle:', this.currentProductHandle);
    console.log('Available handles:', csvData.map(row => row.Handle));

    // Find the row that matches the current product handle
    const productRow = csvData.find(row => row.Handle === this.currentProductHandle);
    
    if (productRow && productRow.Sizing_Table) {
      console.log('Found matching row for handle:', this.currentProductHandle);
      const sizeChartHtml = productRow.Sizing_Table;
      const howToMeasureHtml = `
        <div class="how-to-measure">
          <h3>How to Measure</h3>
          <div class="measurement-instructions">
            <p><strong>Chest/Bust:</strong> Measure around the fullest part of your chest/bust, keeping the measuring tape horizontal.</p>
            <p><strong>Waist:</strong> Measure around your natural waistline, keeping the tape comfortably loose.</p>
            <p><strong>Hip:</strong> Measure around the fullest part of your hips, approximately 8" below your waistline.</p>
          </div>
        </div>
      `;
      return `<div class="size-chart-content">${sizeChartHtml}${howToMeasureHtml}</div>`;
    } else {
      console.error('No matching row found for handle:', this.currentProductHandle);
      
      // Show debug information
      const availableHandles = csvData.map(row => row.Handle).slice(0, 5).join(', ');
      return `<div class="error-message">
        No size chart available for this product.<br>
        <small>Debug: Looking for "${this.currentProductHandle}"<br>
        Available handles: ${availableHandles}...</small>
      </div>`;
    }
  }

  initializeCategories() {
    // No categories to initialize since we only have women's sizes
    return;
  }

  async openPopup(event) {
    if (event) {
      event.preventDefault();
    }
    
    // Load the content dynamically when opening
    const body = this.popupContainer.querySelector('.size-chart-popup__body');
    body.innerHTML = '<div class="loading-message">Loading size chart...</div>';
    
    this.popupContainer.classList.add('active');
    document.body.style.overflow = 'hidden';
    
    // Fetch and display the content
    const content = await this.getSizeChartContent();
    body.innerHTML = content;
  }

  closePopup() {
    this.popupContainer.classList.remove('active');
    document.body.style.overflow = '';
  }
}

// Initialize when DOM is loaded
document.addEventListener('DOMContentLoaded', () => {
  new SizeChartPopup();
});

// For Shopify's section/page reload
document.addEventListener('shopify:section:load', () => {
  new SizeChartPopup();
});
function showSection(section) {
  const isTops = section === 'Tops';
  document.getElementById("topsSection").style.display = isTops ? "block" : "none";
  document.getElementById("pantsSection").style.display = isTops ? "none" : "block";
  document.getElementById("mainTabTops").style.backgroundColor = isTops ? "#f2f2f2" : "#fff";
  document.getElementById("mainTabPants").style.backgroundColor = isTops ? "#fff" : "#f2f2f2";
}
function showTable(unit) {
  document.getElementById("sizeTable").style.display = unit === 'size' ? 'block' : 'none';
  document.getElementById("cmTable").style.display = unit === 'cm' ? 'block' : 'none';
  document.getElementById("inTable").style.display = unit === 'in' ? 'block' : 'none';

  document.getElementById("sizeTab").style.backgroundColor = unit === 'size' ? '#f2f2f2' : '#fff';
  document.getElementById("cmTab").style.backgroundColor = unit === 'cm' ? '#f2f2f2' : '#fff';
  document.getElementById("inTab").style.backgroundColor = unit === 'in' ? '#f2f2f2' : '#fff';
}

  function showMainTab(tab) {
    document.getElementById("topChart").style.display = tab === 'top' ? 'block' : 'none';
    document.getElementById("pantChart").style.display = tab === 'pant' ? 'block' : 'none';
    document.getElementById("mainTabTop").style.backgroundColor = tab === 'top' ? '#f2f2f2' : '#fff';
    document.getElementById("mainTabPant").style.backgroundColor = tab === 'pant' ? '#f2f2f2' : '#fff';
  }
  function showChartTab(section, unit) {
    ["size", "cm", "in"].forEach(function(type) {
      document.getElementById(`chartTable${type.charAt(0).toUpperCase() + type.slice(1)}${section.charAt(0).toUpperCase() + section.slice(1)}`).style.display = type === unit ? 'block' : 'none';
      document.getElementById(`chartTab${type.charAt(0).toUpperCase() + type.slice(1)}${section.charAt(0).toUpperCase() + section.slice(1)}`).style.backgroundColor = type === unit ? '#f2f2f2' : '#fff';
    });
  }
function showTable2(unit, section) {
  ["size", "cm", "in"].forEach(function(type) {
    document.getElementById(`${type}Table${section}`).style.display = type === unit ? 'block' : 'none';
    document.getElementById(`${type}Tab${section}`).style.backgroundColor = type === unit ? '#f2f2f2' : '#fff';
  });
}
